import { Evaluacion } from '@/app/models/interface/evaluacion.model';
import { environment } from '@/environments/environment';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { catchError, map } from 'rxjs';
import { handleError } from '../tools/handle-error.tools';
import { Participantes } from '@/app/models/interface/participante.model';
import { Adjunto } from '@/app/models/interface/adjunto.model';
import {
  PaginatorModel,
  HasPagination,
} from '@/app/models/interface/paginator.model';
import { CustomResponse } from '@/app/models/interface/response.model';
import { CapacitacionFaltante } from '@/app/models/interface/capacitacion-faltante';

@Injectable({ providedIn: 'root' })
export class EvaluacionService {
  private readonly http = inject(HttpClient);
  private readonly url = environment.api + 'evaluacion/';

  create(data: FormData) {
    return this.http.post<CustomResponse<Evaluacion>>(this.url, data).pipe(
      catchError((err) => handleError(err)),
      map((data) => {
        const d = data.result;

        return {
          ...d,
        };
      }),
    );
  }

  findAll(params?: Params) {
    return this.http
      .get<
        CustomResponse<PaginatorModel<Evaluacion[], HasPagination>>
      >(this.url, { params })
      .pipe(
        catchError((err) => handleError(err)),
        map((data) => {
          const d = data.result;
          if (d && Array.isArray(d.data)) {
            return {
              ...d,
              data: d.data.map((d) => ({
                ...d,
                capacitacion: {
                  ...d.capacitacion,
                  actividades:
                    d.capacitacion.actividadCompetenciaCapacitaciones.map(
                      (acc) => acc.actividad,
                    ),
                  competencia:
                    d.capacitacion.actividadCompetenciaCapacitaciones.map(
                      (acc) => acc.competencia,
                    ),
                },
              })),
            };
          }
          return d;
        }),
      );
  }

  getUserActualEvaluaciones(params?: Params) {
    const dir = this.url + 'getUserActualEvaluaciones';
    return this.http
      .get<CustomResponse<PaginatorModel<Evaluacion[], HasPagination>>>(dir, {
        params,
      })
      .pipe(
        catchError((err) => handleError(err)),
        map((data) => {
          const d = data.result;
          if (d && Array.isArray(d.data)) {
            return {
              ...d,
              data: d.data.map((d) => ({
                ...d,
                capacitacion: {
                  ...d.capacitacion,
                  actividades:
                    d.capacitacion.actividadCompetenciaCapacitaciones.map(
                      (acc) => acc.actividad,
                    ),
                  competencia:
                    d.capacitacion.actividadCompetenciaCapacitaciones.map(
                      (acc) => acc.competencia,
                    ),
                },
              })),
            };
          }
          return d;
        }),
      );
  }

  findOne(id: string) {
    const url = this.url + id;
    return this.http.get<CustomResponse<Evaluacion>>(url).pipe(
      catchError((err) => handleError(err)),
      map((data) => {
        const d = data.result;
        if (d) {
          const usuarios =
            d.participantes?.map((participante) => participante.usuario) || [];
          return {
            ...d,
            usuarios,
            capacitacion: {
              ...d.capacitacion,
              actividades:
                d.capacitacion.actividadCompetenciaCapacitaciones.map(
                  (acc) => acc.actividad,
                ),
              competencia:
                d.capacitacion.actividadCompetenciaCapacitaciones.map(
                  (acc) => acc.competencia,
                ),
            },
          };
        }
        return d;
      }),
    );
  }

  update(item: Partial<Evaluacion>) {
    const url = this.url + item.id;
    return this.http.put<CustomResponse<Evaluacion>>(url, item).pipe(
      catchError((err) => handleError(err)),
      map((data) => {
        const d = data.result;
        if (d) {
          return {
            ...d,
            capacitacion: {
              ...d.capacitacion,
              actividades:
                d.capacitacion.actividadCompetenciaCapacitaciones.map(
                  (acc) => acc.actividad,
                ),
              competencia:
                d.capacitacion.actividadCompetenciaCapacitaciones.map(
                  (acc) => acc.competencia,
                ),
            },
          };
        }
        return d;
      }),
    );
  }

  getUserEvaluacion(id: string, idUsuario: number) {
    const dir = `${this.url}user/${id}/${idUsuario}`;
    return this.http.get<CustomResponse<Evaluacion>>(dir).pipe(
      catchError((err) => handleError(err)),
      map((data) => data.result),
    );
  }

  getCapacitacionesFaltantes(idUsuario: number) {
    const dir = `${this.url}user/faltantes/${idUsuario}`;
    return this.http.get<CustomResponse<CapacitacionFaltante[]>>(dir).pipe(
      catchError((err) => handleError(err)),
      map((data) => data.result),
    );
  }

  getEvaluacionForPdf(id: string) {
    const dir = `${this.url}user/${id}`;
    return this.http.get<CustomResponse<Evaluacion>>(dir).pipe(
      catchError((err) => handleError(err)),
      map((data) => data.result),
    );
  }

  sendManualNotificaciones(id: string, participantesId: number[]) {
    const dir = this.url + 'sendManualNotificaciones/' + id;
    return this.http
      .post<any>(dir, { participantesId })
      .pipe(catchError((err) => handleError(err)));
  }

  uploadArchivos(formData: FormData, id: string) {
    const dir = this.url + 'newArchivos/' + id;
    return this.http.post<CustomResponse<Adjunto[]>>(dir, formData).pipe(
      catchError((err) => handleError(err)),
      map((data) => data.result),
    );
  }

  getParticipantes(params: Params) {
    const dir = this.url + 'getParticipantes';
    return this.http
      .get<
        CustomResponse<PaginatorModel<Participantes[], HasPagination>>
      >(dir, { params })
      .pipe(
        catchError((err) => handleError(err)),
        map((data) => data.result),
      );
  }
}
